import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Moon, Sun, User as UserIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useTheme } from "@/lib/theme-provider";
import { cn } from "@/lib/utils";

/**
 * Sticky glass top bar. Title is either a Fraunces display heading (dashboard)
 * or plain Inter (interior screens). The theme toggle lives here on top-level
 * screens so users can flip without opening Profile.
 */

interface NavHeaderProps {
  title: string;
  display?: boolean;
  showBack?: boolean;
  showThemeToggle?: boolean;
  right?: ReactNode;
  /**
   * Caps and centers the header's title/actions row so it lines up with
   * the page content below on wide (desktop) viewports instead of
   * stretching edge-to-edge. Match whatever max-width the route's own
   * content uses. The blurred bar itself always stays full-bleed.
   */
  maxWidthClassName?: string;
}

export function NavHeader({
  title,
  display = false,
  showBack = false,
  showThemeToggle = false,
  right,
  maxWidthClassName = "max-w-2xl",
}: NavHeaderProps) {
  const navigate = useNavigate();
  const { resolved, toggle } = useTheme();

  return (
    <header
      // Sticks just below the status bar (see the scrim in main.tsx), not at
      // the true viewport top where it would slide under the system clock.
      // data-ad-clip: in-feed banners are clipped below this bar.
      data-ad-clip="top"
      className={cn(
        "sticky top-[var(--safe-top)] z-20 px-5 pt-3 pb-2",
        "bg-[color-mix(in_oklab,var(--bg)_80%,transparent)] backdrop-blur-lg",
        "border-b border-line",
      )}
    >
      <div className={cn("mx-auto flex w-full items-center justify-between", maxWidthClassName)}>
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {showBack ? (
            <button
              type="button"
              onClick={() => navigate(-1)}
              aria-label="Back"
              className="-ml-2 flex h-11 w-11 items-center justify-center rounded-full text-ink hover:bg-bg-sunken"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          ) : null}
          <h1
            className={cn(
              "truncate text-ink m-0",
              display
                ? "font-display text-[30px] leading-none tracking-tight"
                : "font-sans text-[18px] font-semibold",
            )}
            style={
              display
                ? { fontVariationSettings: `"opsz" 48, "wght" 500` }
                : undefined
            }
          >
            {title}
          </h1>
        </div>

        <div className="flex items-center gap-1">
          {showThemeToggle ? (
            <button
              type="button"
              onClick={toggle}
              aria-label="Toggle theme"
              className="flex h-11 w-11 items-center justify-center rounded-full text-ink hover:bg-bg-sunken"
            >
              {resolved === "dark" ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </button>
          ) : null}
          {right ?? (
            <Link
              to="/profile"
              aria-label="Profile"
              className="flex h-11 w-11 items-center justify-center rounded-full text-ink hover:bg-bg-sunken"
            >
              <UserIcon className="h-5 w-5" />
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
